import { createSlice } from '@reduxjs/toolkit'
import { register } from '../../../helpers/reducerRegistry'
import { keyById } from '../../common/popupCompanyList/listCompany/helper'
import { DISPLAY_TYPES } from '../constants'
import {
  getKeyByTypePrefix,
  handleChartData,
  handleReportYear,
  handleTableData,
  handleTotalCompany,
} from './helper'
import {
  getReportYear,
  postAssetManagementEfficiency,
  postAssetQuality,
  postBusinessGrowth,
  postBusinessPosition,
  postCapitalAdequacy,
  postCapitalEarningLeverage,
  postEarningsQuality,
  postFundingLiquidity,
  postHightLight,
  postLiquiditySolvencyAndCoverage,
  postOrganizationInfo,
  postProfitability,
  postTotalCompany,
} from './thunk'

const { CORPORATE } = DISPLAY_TYPES
export const DEFAULT_TOTAL_COMPANY = {
  totalCompany: undefined,
  totalCount: undefined,
}
export const DEFAULT_TABLE_DATA = {
  tableHeader: [],
  dataByRowId: {},
  ids: [],
}
export const DEFAULT_CHART_DATA = { chartData: [], titleById: {} }

const initialState = {
  displayType: CORPORATE,
  compareLeft: undefined,
  compareRight: undefined,
  reportYear: 0,
  totalCompany: DEFAULT_TOTAL_COMPANY,
  rawTotalCompany: [],
  hightLight: [],
  listCompanyDetailLoading: true,
  listCompanyDetail: {},
  idsCompanyDetail: [],
  companyDetailPagination: {},
  filter: {
    page: 1,
    pageSize: 20,
  },
  tableData: {
    postLiquiditySolvencyAndCoverage: DEFAULT_TABLE_DATA,
    postAssetManagementEfficiency: DEFAULT_TABLE_DATA,
    postProfitability: DEFAULT_TABLE_DATA,
    postBusinessGrowth: DEFAULT_TABLE_DATA,
    postCapitalAdequacy: DEFAULT_TABLE_DATA,
    postAssetQuality: DEFAULT_TABLE_DATA,
    postEarningsQuality: DEFAULT_TABLE_DATA,
    postFundingLiquidity: DEFAULT_TABLE_DATA,
    postBusinessPosition: DEFAULT_TABLE_DATA,
    postCapitalEarningLeverage: DEFAULT_TABLE_DATA,
  },
  chartData: {
    postLiquiditySolvencyAndCoverage: DEFAULT_CHART_DATA,
    postAssetManagementEfficiency: DEFAULT_CHART_DATA,
    postProfitability: DEFAULT_CHART_DATA,
    postBusinessGrowth: DEFAULT_CHART_DATA,
    postCapitalAdequacy: DEFAULT_CHART_DATA,
    postAssetQuality: DEFAULT_CHART_DATA,
    postEarningsQuality: DEFAULT_CHART_DATA,
    postFundingLiquidity: DEFAULT_CHART_DATA,
    postBusinessPosition: DEFAULT_CHART_DATA,
    postCapitalEarningLeverage: DEFAULT_CHART_DATA,
  },
  loadings: {
    postTotalCompany: true,
    postHightLight: true,
    postLiquiditySolvencyAndCoverage: true,
    postAssetManagementEfficiency: true,
    postProfitability: true,
    postBusinessGrowth: true,
    postCapitalAdequacy: true,
    postAssetQuality: true,
    postEarningsQuality: true,
    postFundingLiquidity: true,
    postBusinessPosition: true,
    postCapitalEarningLeverage: true,
  },
  activeRow: {
    postLiquiditySolvencyAndCoverage: undefined,
    postAssetManagementEfficiency: undefined,
    postProfitability: undefined,
    postBusinessGrowth: undefined,
    postCapitalAdequacy: undefined,
    postAssetQuality: undefined,
    postEarningsQuality: undefined,
    postFundingLiquidity: undefined,
    postBusinessPosition: undefined,
    postCapitalEarningLeverage: undefined,
  },
}

const slice = createSlice({
  name: 'portfolioInsights/finance',
  initialState,
  reducers: {
    changeDisplayType: (state, action) => {
      state.displayType = action.payload
    },
    changeCompareLeft: (state, action) => {
      state.compareLeft = action.payload
    },
    changeCompareRight: (state, action) => {
      state.compareRight = action.payload
    },
    handleScrollActiveRow: (state, action) => {
      const { keyTypePrefix, rowId } = action.payload
      const newIds = [
        rowId,
        ...state.tableData[keyTypePrefix].ids.filter((id) => id !== rowId),
      ]

      state.activeRow[keyTypePrefix] = rowId
      state.tableData[keyTypePrefix].ids = newIds
    },
    changeFilter: (state, action) => {
      state.filter = { ...state.filter, ...action.payload }
    },
    resetData: (state, action) => {
      const key = getKeyByTypePrefix(action.payload)
      state.tableData[key] = DEFAULT_TABLE_DATA
      state.chartData[key] = DEFAULT_CHART_DATA
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getReportYear.fulfilled, (state, action) => {
      state.reportYear = handleReportYear(action.payload)
    })
    builder.addCase(postOrganizationInfo.pending, (state) => {
      state.listCompanyDetailLoading = true
    })
    builder.addCase(postOrganizationInfo.fulfilled, (state, action) => {
      state.listCompanyDetail = keyById(action.payload.data)
      state.idsCompanyDetail = Object.keys(state.listCompanyDetail)
      state.companyDetailPagination = action.payload
      state.listCompanyDetailLoading = false
    })
    builder.addCase(postOrganizationInfo.rejected, (state, action) => {
      state.listCompanyDetailLoading = action.payload.loading
    })
    builder.addCase(postTotalCompany.pending, (state) => {
      state.loadings.postTotalCompany = true
    })
    builder.addCase(postTotalCompany.fulfilled, (state, action) => {
      state.totalCompany = handleTotalCompany(action.payload, state.displayType)
      state.rawTotalCompany = action.payload || []
      state.loadings.postTotalCompany = false
    })
    builder.addCase(postTotalCompany.rejected, (state, action) => {
      state.loadings.postTotalCompany = action.payload.loading
    })
    builder.addCase(postHightLight.pending, (state) => {
      state.loadings.postHightLight = true
    })
    builder.addCase(postHightLight.fulfilled, (state, action) => {
      state.hightLight = action.payload
      state.loadings.postHightLight = false
    })
    builder.addCase(postHightLight.rejected, (state, action) => {
      state.loadings.postHightLight = action.payload.loading
    })
    builder.addCase(postLiquiditySolvencyAndCoverage.pending, (state) => {
      state.loadings.postLiquiditySolvencyAndCoverage = true
    })
    builder.addCase(
      postLiquiditySolvencyAndCoverage.fulfilled,
      (state, action) => {
        state.tableData.postLiquiditySolvencyAndCoverage = handleTableData(
          action.payload,
          action.meta.arg,
        )
        state.chartData.postLiquiditySolvencyAndCoverage = handleChartData(
          action.payload,
        )
        state.activeRow.postLiquiditySolvencyAndCoverage =
          state.tableData.postLiquiditySolvencyAndCoverage.ids[0]
        state.loadings.postLiquiditySolvencyAndCoverage = false
      },
    )
    builder.addCase(
      postLiquiditySolvencyAndCoverage.rejected,
      (state, action) => {
        state.loadings.postLiquiditySolvencyAndCoverage = action.payload.loading
      },
    )
    builder.addCase(postAssetManagementEfficiency.pending, (state) => {
      state.loadings.postAssetManagementEfficiency = true
    })
    builder.addCase(
      postAssetManagementEfficiency.fulfilled,
      (state, action) => {
        state.tableData.postAssetManagementEfficiency = handleTableData(
          action.payload,
          action.meta.arg,
        )
        state.chartData.postAssetManagementEfficiency = handleChartData(
          action.payload,
        )
        state.activeRow.postAssetManagementEfficiency =
          state.tableData.postAssetManagementEfficiency.ids[0]
        state.loadings.postAssetManagementEfficiency = false
      },
    )
    builder.addCase(postAssetManagementEfficiency.rejected, (state, action) => {
      state.loadings.postAssetManagementEfficiency = action.payload.loading
    })
    builder.addCase(postProfitability.pending, (state) => {
      state.loadings.postProfitability = true
    })
    builder.addCase(postProfitability.fulfilled, (state, action) => {
      state.tableData.postProfitability = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postProfitability = handleChartData(action.payload)
      state.activeRow.postProfitability =
        state.tableData.postProfitability.ids[0]
      state.loadings.postProfitability = false
    })
    builder.addCase(postProfitability.rejected, (state, action) => {
      state.loadings.postProfitability = action.payload.loading
    })
    builder.addCase(postBusinessGrowth.pending, (state) => {
      state.loadings.postBusinessGrowth = true
    })
    builder.addCase(postBusinessGrowth.fulfilled, (state, action) => {
      state.tableData.postBusinessGrowth = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postBusinessGrowth = handleChartData(action.payload)
      state.activeRow.postBusinessGrowth =
        state.tableData.postBusinessGrowth.ids[0]
      state.loadings.postBusinessGrowth = false
    })
    builder.addCase(postBusinessGrowth.rejected, (state, action) => {
      state.loadings.postBusinessGrowth = action.payload.loading
    })
    builder.addCase(postCapitalAdequacy.pending, (state) => {
      state.loadings.postCapitalAdequacy = true
    })
    builder.addCase(postCapitalAdequacy.fulfilled, (state, action) => {
      state.tableData.postCapitalAdequacy = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postCapitalAdequacy = handleChartData(action.payload)
      state.activeRow.postCapitalAdequacy =
        state.tableData.postCapitalAdequacy.ids[0]
      state.loadings.postCapitalAdequacy = false
    })
    builder.addCase(postCapitalAdequacy.rejected, (state, action) => {
      state.loadings.postCapitalAdequacy = action.payload.loading
    })
    builder.addCase(postAssetQuality.pending, (state) => {
      state.loadings.postAssetQuality = true
    })
    builder.addCase(postAssetQuality.fulfilled, (state, action) => {
      state.tableData.postAssetQuality = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postAssetQuality = handleChartData(action.payload)
      state.activeRow.postAssetQuality = state.tableData.postAssetQuality.ids[0]
      state.loadings.postAssetQuality = false
    })
    builder.addCase(postAssetQuality.rejected, (state, action) => {
      state.loadings.postAssetQuality = action.payload.loading
    })
    builder.addCase(postEarningsQuality.pending, (state) => {
      state.loadings.postEarningsQuality = true
    })
    builder.addCase(postEarningsQuality.fulfilled, (state, action) => {
      state.tableData.postEarningsQuality = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postEarningsQuality = handleChartData(action.payload)
      state.activeRow.postEarningsQuality =
        state.tableData.postEarningsQuality.ids[0]
      state.loadings.postEarningsQuality = false
    })
    builder.addCase(postEarningsQuality.rejected, (state, action) => {
      state.loadings.postEarningsQuality = action.payload.loading
    })
    builder.addCase(postFundingLiquidity.pending, (state) => {
      state.loadings.postFundingLiquidity = true
    })
    builder.addCase(postFundingLiquidity.fulfilled, (state, action) => {
      state.tableData.postFundingLiquidity = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postFundingLiquidity = handleChartData(action.payload)
      state.activeRow.postFundingLiquidity =
        state.tableData.postFundingLiquidity.ids[0]
      state.loadings.postFundingLiquidity = false
    })
    builder.addCase(postFundingLiquidity.rejected, (state, action) => {
      state.loadings.postFundingLiquidity = action.payload.loading
    })
    builder.addCase(postBusinessPosition.pending, (state) => {
      state.loadings.postBusinessPosition = true
    })
    builder.addCase(postBusinessPosition.fulfilled, (state, action) => {
      state.tableData.postBusinessPosition = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postBusinessPosition = handleChartData(action.payload)
      state.activeRow.postBusinessPosition =
        state.tableData.postBusinessPosition.ids[0]
      state.loadings.postBusinessPosition = false
    })
    builder.addCase(postBusinessPosition.rejected, (state, action) => {
      state.loadings.postBusinessPosition = action.payload.loading
    })
    builder.addCase(postCapitalEarningLeverage.pending, (state) => {
      state.loadings.postCapitalEarningLeverage = true
    })
    builder.addCase(postCapitalEarningLeverage.fulfilled, (state, action) => {
      state.tableData.postCapitalEarningLeverage = handleTableData(
        action.payload,
        action.meta.arg,
      )
      state.chartData.postCapitalEarningLeverage = handleChartData(
        action.payload,
      )
      state.activeRow.postCapitalEarningLeverage =
        state.tableData.postCapitalEarningLeverage.ids[0]
      state.loadings.postCapitalEarningLeverage = false
    })
    builder.addCase(postCapitalEarningLeverage.rejected, (state, action) => {
      state.loadings.postCapitalEarningLeverage = action.payload.loading
    })
  },
})

export const {
  changeDisplayType,
  changeCompareLeft,
  changeCompareRight,
  handleScrollActiveRow,
  changeFilter,
  resetData,
} = slice.actions

export const selectDisplayType = (state) => state[slice.name].displayType
export const selectCompareLeft = (state) => state[slice.name].compareLeft
export const selectCompareRight = (state) => state[slice.name].compareRight
export const selectCellData = (rowId, index, colId, typePrefix) => {
  const dataKey = getKeyByTypePrefix(typePrefix)
  return (state) => {
    return state[slice.name].tableData[dataKey]['dataByRowId'][rowId][index][
      colId
    ]
  }
}
export const selectRowGroup = (rowId, typePrefix) => {
  const dataKey = getKeyByTypePrefix(typePrefix)
  return (state) => state[slice.name].tableData[dataKey]['dataByRowId'][rowId]
}
export const selectAllPortfolio = (state) => state[slice.name].allPortfolio
export const selectReportYear = (state) => state[slice.name].reportYear
export const selectTotalCompany = (state) => state[slice.name].totalCompany
export const selectHightLight = (state) => state[slice.name].hightLight
export const selectLoading = (typePrefix) => (state) => {
  const loadingKey = getKeyByTypePrefix(typePrefix)
  return state[slice.name].loadings[loadingKey]
}
export const selectTableData = (typePrefix) => (state) => {
  const dataKey = getKeyByTypePrefix(typePrefix)
  return state[slice.name].tableData[dataKey]
}
export const selectActiveRow = (typePrefix) => (state) => {
  const dataKey = getKeyByTypePrefix(typePrefix)
  return state[slice.name].activeRow[dataKey]
}
export const selectChartData = (typePrefix) => (state) => {
  const dataKey = getKeyByTypePrefix(typePrefix)
  return state[slice.name].chartData[dataKey]
}
export const selectCompanyDetailCell = (rowId, colId) => (state) => {
  return state[slice.name].listCompanyDetail[rowId][colId]
}
export const selectCompanyDetailIds = (state) => {
  return state[slice.name].idsCompanyDetail
}
export const selectListCompanyDetailLoading = (state) =>
  state[slice.name].listCompanyDetailLoading
export const selectCompanyDetailPagination = (state) =>
  state[slice.name].companyDetailPagination
export const selectFilter = (state) => state[slice.name].filter
export const selectRawTotalCompany = (state) =>
  state[slice.name].rawTotalCompany

register(slice.name, slice.reducer)
